/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Heading,
  Text,
  Spinner,
  VStack,
  Icon,
  useToast
} from "@chakra-ui/react";
import { LockIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useModalToast } from "../context/GlobalModalToast";

const SsoCallback = () => {
  const [status, setStatus] = useState("Validating secure credentials...");
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { toast } = useModalToast();

  useEffect(() => {
    const authenticateSSO = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token) {
          setErrorMsg("Access Denied: SSO cryptographic token is missing.");
          return;
        }

        setStatus("Decoding secure corporate signature...");
        
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/sso/verify`, {
          token
        });

        if (response.data && response.data.success) {
          setStatus("Identity validated! Provisioning curriculum profile...");
          
          const { token: localToken, user } = response.data;
          
          const sessionPayload = {
            token: localToken,
            userId: user._id,
            name: user.fullName,
            email: user.email,
            role: user.role,
            department: user.department,
            project: user.project,
            companyId: user.companyId,
            companyName: user.companyName,
            user: {
              _id: user._id,
              fullName: user.fullName,
              email: user.email,
              role: user.role,
              companyId: user.companyId,
              companyName: user.companyName,
              department: user.department,
              hasCompletedLna: user.hasCompletedLna
            },
            msg: "SSO Login Successful"
          };

          // --- Session Storage Isolation (Instant Logout on Tab Close) ---
          sessionStorage.setItem("isSsoSession", "true");
          sessionStorage.setItem("user", JSON.stringify(sessionPayload));
          
          // Write to localStorage for existing application queries compatibility
          localStorage.setItem("user", JSON.stringify(sessionPayload));

          // Sync into Redux store
          dispatch({
            type: "LOGIN_SUCCESS", // Standard action type inside MERN auth Redux
            payload: sessionPayload
          });

          toast({
            title: "Access Granted",
            description: `Logged in as ${user.fullName} (${user.department || "Global"})`,
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right"
          });

          // Route to appropriate dashboard
          if (user.role === "admin" || user.role === "super_admin") {
            navigate("/admin/dashboard");
          } else if (user.role === "teacher" || user.role === "instructor") {
            navigate("/teacher-dashboard");
          } else {
            navigate("/home");
          }
        } else {
          setErrorMsg("Authentication rejected by central server.");
        }
      } catch (err) {
        console.error("SSO Callback Error:", err);
        setErrorMsg(
          err.response?.data?.message || 
          "Failed to establish asymmetric trust with HCM database. Request expired or invalid network IP."
        );
      }
    };

    authenticateSSO();
  }, [location, navigate, dispatch, toast]);

  return (
    <Flex
      w="100vw"
      h="100vh"
      align="center"
      justify="center"
      bgGradient="linear(to-br, purple.600, blue.600)"
      p={4}
    >
      <Box
        className="glass-card"
        p={8}
        maxW="460px"
        w="full"
        borderRadius="3xl"
        bg="rgba(255, 255, 255, 0.12)"
        border="1px solid rgba(255, 255, 255, 0.2)"
        boxShadow="0 20px 40px rgba(0, 0, 0, 0.3)"
        backdropFilter="blur(25px)"
        textAlign="center"
        color="white"
      >
        <VStack spacing={6}>
          <Flex
            bg="rgba(255, 255, 255, 0.2)"
            p={4}
            borderRadius="2xl"
            boxShadow="inner"
            align="center"
            justify="center"
          >
            <Icon as={LockIcon} boxSize={8} color="white" />
          </Flex>

          <Heading size="md" fontWeight="800" letterSpacing="-0.5px">
            ERAM SKILLUP SSO
          </Heading>

          {errorMsg ? (
            <VStack spacing={3}>
              <Text fontSize="xs" fontWeight="bold" color="red.300" bg="rgba(224, 86, 86, 0.15)" p={3} borderRadius="xl">
                ⚠️ {errorMsg}
              </Text>
              <Text fontSize="xs" color="whiteAlpha.700">
                Please re-authenticate through the main HCM central portal.
              </Text>
            </VStack>
          ) : (
            <VStack spacing={4}>
              <Spinner size="lg" color="white" thickness="4px" />
              <Text fontSize="xs" fontWeight="700" letterSpacing="0.5px" color="whiteAlpha.900">
                {status.toUpperCase()}
              </Text>
            </VStack>
          )}

          <Text fontSize="10px" color="whiteAlpha.600">
            Secure Cryptographic SSO Integration • ERAM Group
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default SsoCallback;
